import Big from 'big.js';
import { DateTime } from 'luxon';
import * as session from 'smg/admin/session';
import Batch from 'smg/batch';
import * as html from 'smg/html';
import * as models from 'smg/models';
import { IsObject } from 'smg/object';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { ArtistRewards, CacheKey, Details, Orders, Realms } from '../index/data';
import * as kpiCards from '../index/kpicards';
import * as monthlyQuotas from '../index/monthlyquotas';
import * as productLeaderboard from '../index/productleaderboard';
import * as salesTeamLeaderboard from '../index/salesteamleaderboard';
import * as accounts from '../accounts';
import { Relref } from '../hugo';
import i18n from '../i18n';

declare interface myWindow extends Window {
    readonly login: string;
}
declare let window: myWindow;

const statsInterval = 10 * 60 * 1000;

async function main() {
    await session.Session().catch(e => {
        if (IsObject(e) && IsObject(e['response']) && e['response']['status'] === 403) {
            // Stay on current language (window.login is language-prefixed by Hugo).
            window.location.href = window.login;
            return;
        } else {
            toast.Error(i18n('somethingWentWrong'));
            return;
        }
    });

    // Factory home is v3 unconfirmed — do not bounce through IAP v2 /factoryorders
    // (that redirects via IAP_V3_ROOT and drops the language prefix + can hit the wrong port).
    if (await session.HasRole(models.AdminRoleID.ADMIN_ROLES_FACTORY)) {
        window.location.href = accounts.WithProfile(Relref('/orders/unconfirmed'));
        return;
    }

    if (await session.HasRole(models.AdminRoleID.ADMIN_ROLES_ARTIST) && !await session.HasRole(models.AdminRoleID.ADMIN_ROLES_SALES) &&
        !await session.HasRole(models.AdminRoleID.ADMIN_ROLES_MARKETING) && !await session.HasRole(models.AdminRoleID.ADMIN_ROLES_ARTIST_STAT_VIEWER)) {
        window.location.href = accounts.WithProfile(Relref('/friday'));
        return;
    }

    if (await session.HasPerm('r/orders') || await session.HasPerm('r/stats/leaderboard')) {
        void salesKpis();
    }

    if (await session.HasRole(models.AdminRoleID.ADMIN_ROLES_ARTIST_STAT_VIEWER)) {
        artistKpis();
    }
}

async function salesKpis() {
    const salesTeamLeaderboardParent = document.querySelector('#sales-team-leaderboard-parent') as HTMLElement;
    const monthlyQuotasParent = document.querySelector('#monthly-quotas-parent') as HTMLElement;
    const productLeaderboardParent = document.querySelector('#product-leaderboards-parent') as HTMLElement;

    salesTeamLeaderboardParent.classList.remove('d-none');
    monthlyQuotasParent.classList.remove('d-none');
    productLeaderboardParent.classList.remove('d-none');

    const b = new Batch();
    const curr = Orders(DateTime.now(), false, b);
    const prev = Orders(DateTime.now().minus({ year: 1 }), false, b);
    const details = Details(b);
    const realms = Realms(b);

    void b.exec();

    await Promise.all([curr, prev, details, realms]);

    await Promise.all([
        salesTeamLeaderboard.SalesTeamLeaderboard(salesTeamLeaderboardParent),
        productLeaderboard.ProductLeaderboard(productLeaderboardParent, 0),
        monthlyQuotas.MonthlyQuotas(
            monthlyQuotasParent,
            await (await curr).get(DateTime.now().toFormat(CacheKey)) ?? [],
            await (await prev).get(DateTime.now().minus({ year: 1 }).toFormat(CacheKey)) ?? [],
            await realms,
            await details,
            0,
        ),
        kpiCards.KPICards(Promise.resolve(await (await curr).get(DateTime.now().toFormat(CacheKey)) ?? [])),
    ]);

    const refresh = async () => {
        const b = new Batch();
        const curr = Orders(DateTime.now(), true, b);

        await b.exec();

        const thisMonthsOrders = Promise.resolve(await (await curr).get(DateTime.now().toFormat(CacheKey)) ?? []);

        await Promise.all([
            salesTeamLeaderboard.SalesTeamLeaderboard(salesTeamLeaderboardParent),
            productLeaderboard.ProductLeaderboard(productLeaderboardParent, productLeaderboard.Options.currentIndex, productLeaderboard.Options.currentDesc, productLeaderboard.Options.product),
            monthlyQuotas.MonthlyQuotas(
                monthlyQuotasParent,
                await (await curr).get(DateTime.now().toFormat(CacheKey)) ?? [],
                await (await prev).get(DateTime.now().minus({ year: 1 }).toFormat(CacheKey)) ?? [],
                await realms,
                await details,
                monthlyQuotas.Options.currentIndex,
                monthlyQuotas.Options.currentDesc,
            ),
            kpiCards.KPICards(thisMonthsOrders),
        ]);
    };

    setInterval(() => {
        if (document.hidden) {
            return;
        }
        void refresh();
    }, statsInterval);

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') {
            return;
        }
        void refresh();
    });
}

function artistKpis() {
    const artistRewards = async (breakCache: boolean) => {
        const artistRewardParent = document.querySelector('#artist-rewards-parent') as HTMLElement;
        artistRewardParent.classList.remove('d-none');

        const b = new Batch();
        const rewards = ArtistRewards(b, breakCache);
        await b.exec();

        const card = template.Clone('#artist-rewards');
        const table = template.Clone('#artist-rewards-table');
        const tableBody = table.querySelector('tbody') as HTMLElement;

        const createRow = (name: string, points: Big) => {
            tableBody.appendChild(html.FromString(`<tr>
                    <td>${name}</td>
                    <td class="text-end">${points.toFixed(1)}</td>
                </tr>`));
        };

        const a: { name: string; points: Big }[] = [];
        for (const artist of (await rewards).values()) {
            const user = artist[0].user;
            let points = Big(0);
            artist.forEach(r => points = points.add(r.points ?? '0'));
            a.push({ name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`, points });
        }

        a.sort((b, c) => b.points.lt(c.points) ? 1 : -1);
        a.forEach(d => createRow(d.name, d.points));

        card.querySelector('.card-body')?.appendChild(table);
        artistRewardParent.innerHTML = '';
        artistRewardParent.appendChild(card);
    };

    setInterval(() => {
        if (document.hidden) {
            return;
        }
        void artistRewards(true);
    }, statsInterval);

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') {
            return;
        }
        void artistRewards(true);
    });

    void artistRewards(true);
}

void main();